import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  GenericField,
  InfoPanel,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
} from 'generated/axauth-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceData } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import type {
  AuthServiceDetails,
  SubmitResponse,
  WebHookFormData,
} from './ForgotPasswordWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  forgotPasswordWebhook: Yup.string()
    .required('Forgot password webhook URL is required')
    .test(
      'forgot-password-webhook-url',
      'Forgot password webhook must be a valid URL',
      validateUrl,
    ),
});

export const ForgotPasswordWebHookCreate: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [authServiceConfig, setAuthServiceConfig] =
    useState<AuthServiceDetails>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.AXAUTH_SERVICE,
        );
        setAuthServiceConfig(serviceData);
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const saveData = useCallback(
    async (formData: WebHookFormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );
      const response = await envApolloClient.mutate<
        UpdateIdpUserStoreMutation,
        UpdateIdpUserStoreMutationVariables
      >({
        mutation: UpdateIdpUserStoreDocument,
        variables: {
          input: {
            id: userStoreId,
            patch: { forgotPasswordWebhook: formData.forgotPasswordWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, userStoreId],
  );

  const onProceed = useCallback<ActionHandler<WebHookFormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/forgotPassword/details/secret/forgotPassword`,
        );
      }
    },
    [history, envId, userStoreId],
  );

  const Panel: React.FC = () => {
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{authServiceConfig?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">
            {authServiceConfig?.environmentId}
          </Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="forgotPasswordWebhook"
          label="Forgot Password Webhook URL"
          tooltipContent="A webhook that will receive the reset-OTP and handles the user's password reset process. It can be used to customize the password reset experience (i.e. send a password reset email). After the action is confirmed, the [completeEndUserPasswordReset] mutation shall be called to complete the process."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="Forgot Password Webhook Secret">
          <p>Not Generated</p>
        </GenericField>
      </>
    );
  };

  return (
    <>
      <Create<WebHookFormData, SubmitResponse>
        title="Forgot Password Webhook"
        subtitle="Create Forgot Password Webhook"
        validationSchema={webhookValidation}
        initialData={{
          loading,
          error,
        }}
        saveData={saveData}
        infoPanel={<Panel />}
        onProceed={onProceed}
      >
        <Form />
      </Create>
    </>
  );
};

export const ForgotPasswordWebHookCreateBreadCrumb: BreadcrumbResolver = () =>
  'New Forgot Password Webhook';
